// The season timelapse in WAR · SOVEREIGNTY: card 09 of mockup round 076
// (ui-map-interface.md §3.6, §12.1, R1620).
//
// The season series of cells owned becomes the scrubber. The positions
// run from frame 0 to one past the newest frame, and that last position is
// the live map ("now"). While the playhead stands on a frame:
//
// - the map shows the owner of each cell at the frame: the territory pass
//   traces its loops from the frame and the minimap paints it (`setMap`);
// - the hulls are hidden (`showing`, read by the frame loop);
// - a gold band over the map names the frame, and its LIVE gives the map
//   back;
// - the donut, its legend and the heading of the tab follow the frame
//   (`view`, read by `overlay/menus.ts sovereigntyTab`).
//
// PLAY from day 0 plays the sting over the map first (R1655): its canvas
// stands in `stingHost`, a box over the map clear of the dock, under every
// panel of the chrome.
//
// The tab body regenerates its markup on the overlay's 4 Hz guard. The
// transport is the one part a regenerate must not throw away under a drag,
// so it lives in `island`, an element this class keeps for the session:
// the body markup carries `ISLAND_MARK`, and the overlay swaps the markup
// around the island and never takes the island out of the document
// (`overlay/island.ts`).

import { TIMELAPSE_WAR_TICKS } from '../../gen/presentation';
import { timelapse as T } from '../../gen/strings';
import type { Timelapse } from '../../net/timelapse';
import type { GameState } from '../../state';
import { COLS, ROWS } from '../../world';
import { esc } from '../esc';
import {
  dayTicks, fitsGrid, frameCounts, frameStamp, ownersOf, seasonDay, ticksHtml,
} from './model';
import { TimelapsePlayer } from './player';
import { TransportBinding, transportHtml } from './transport';
import { IntroSting } from './intro';

/** The mark the tab markup carries where the island stands. */
export const ISLAND_MARK = '<!--timelapse-island-->';

/** What the map shows for the scrubber: the owner plane of a frame, ridge
 *  cells unowned, or null for the live owners. */
export interface TimelapseMap {
  show(owners: Uint8Array | null): void;
}

/** One frame as WAR · SOVEREIGNTY states it. */
export interface WarFrameView {
  stamp: string;
  tSec: number;
  /** The cells of each owner code, 0 unclaimed (`model.frameCounts`). */
  counts: number[];
}

/** What the tab draws of the timelapse: the frame under the playhead, or
 *  null at the live end. */
export interface WarTimelapseView {
  frame: WarFrameView | null;
}

export interface WarTimelapseDeps {
  state: GameState;
  /** Sends the `TimelapseQuery` of the world socket. */
  query: () => void;
  /** The chrome root: the band stands in it. */
  bandHost: HTMLElement;
  /** A change the tab must redraw: the frame, or a new timelapse. */
  changed: () => void;
  /** True when the last press became a title hold (R1555). */
  titleHeld?: () => boolean;
  /** The scrubber takes the focus (true) when the map starts to show a
   *  frame, and gives it up (false) when the map is live again: LIVE, a
   *  play to the end, a drag to NOW or a hello. The telephone sheet
   *  follows it (ui-map-interface.md §3.6, R1620). */
  focus?: (on: boolean) => void;
  /** The play starts (true) or stops (false): PLAY, PAUSE, a drag, LIVE or
   *  a play that reaches NOW. The telephone sheet collapses while the
   *  player plays, so the map shows whole (§3.6, R1779). */
  play?: (on: boolean) => void;
}

export class WarTimelapse {
  /** The transport, the day marks and the note, kept for the session. */
  readonly island: HTMLElement;
  /** The gold band over the map. */
  readonly band: HTMLElement;
  readonly player = new TimelapsePlayer();
  /** The box of the sting over the map: the map's own width, clear of the
   *  dock (`--dock-w`, as the frame band), under every panel. */
  readonly stingHost: HTMLElement;
  private readonly sting: IntroSting;
  private readonly binding: TransportBinding;
  private tl: Timelapse | null = null;
  private map: TimelapseMap | null = null;
  /** The owner plane of the shown frame, reused from frame to frame. */
  private owners: Uint8Array | null = null;
  /** The frame the map shows, or -1 for the live map. */
  private shown = -1;
  private view_: WarTimelapseView = { frame: null };
  /** The play state `play` last stated. */
  private playing = false;
  private readonly offState: () => void;

  constructor(private readonly d: WarTimelapseDeps) {
    this.island = document.createElement('div');
    this.island.className = 'tlwar';
    this.binding = new TransportBinding(this.island, this.player, {
      stamp: (f) => this.stamp(f),
      held: d.titleHeld,
    });
    this.band = document.createElement('div');
    this.band.id = 'tlband';
    this.band.className = 'panel';
    this.band.hidden = true;
    this.band.innerHTML = `<span class="tag">${esc(T.band)}</span><span class="dot">·</span>`
      + `<b class="tlday"></b><button type="button" data-tl="live">${esc(T.live)}</button>`;
    // The band is fixed chrome, so LIVE acts on the press (§16.8).
    this.band.addEventListener('pointerdown', (ev) => {
      if (ev.button > 0) return;
      if (!(ev.target as Element | null)?.closest?.('[data-tl="live"]')) return;
      ev.preventDefault();
      this.goLive();
    });
    this.band.addEventListener('click', (ev) => {
      if (ev.detail !== 0) return;
      if ((ev.target as Element | null)?.closest?.('[data-tl="live"]')) this.goLive();
    });
    d.bandHost.appendChild(this.band);
    // The first child of the chrome root, so every panel after it stands
    // over the sting; the box itself takes no pointer, and the sting's
    // canvas takes the tap that skips it.
    this.stingHost = document.createElement('div');
    this.stingHost.className = 'tlsting';
    this.stingHost.style.cssText = 'position:absolute;left:0;top:0;bottom:0;'
      + 'right:var(--dock-w, 0px);z-index:0;pointer-events:none';
    d.bandHost.prepend(this.stingHost);
    this.sting = new IntroSting(this.player, () => this.stingHost);
    this.player.on(() => {
      this.follow();
      if (this.player.playing !== this.playing) {
        this.playing = this.player.playing;
        d.play?.(this.playing);
      }
      this.sting.update();
    });
    this.offState = d.state.on(() => this.sync());
  }

  /** Takes the band off the page and stops the clock: a test harness drops
   *  one overlay per case. */
  destroy(): void {
    this.offState();
    this.binding.dispose();
    this.player.dispose();
    this.sting.drop();
    this.band.remove();
    this.stingHost.remove();
  }

  /** True while the map shows a frame: the frame loop hides the hulls. */
  get showing(): boolean { return this.shown >= 0; }

  /** True when the season has a frame that fits the live grid. */
  get has(): boolean { return this.tl !== null; }

  /** The frame under the playhead, for the tab markup. */
  view(): WarTimelapseView { return this.view_; }

  /** The map the frames draw into. main.ts hands it over once. */
  setMap(map: TimelapseMap | null): void {
    this.map = map;
    this.shown = -1;
    this.follow();
  }

  /** The tab opened: ask for the frames of the season so far. The open is
   *  the event, never a timer (§10.3, R83). */
  open(): void { this.d.query(); }

  /** LIVE: the playhead goes to now, and the map comes back. */
  goLive(): void {
    this.player.seek(this.player.max);
  }

  /** Adopts the store's timelapse when it changed. */
  private sync(): void {
    const t = this.d.state.timelapse;
    const fit = fitsGrid(t, COLS, ROWS) ? t : null;
    if (fit === this.tl) return;
    this.tl = fit;
    this.island.innerHTML = fit === null ? '' : this.islandHtml(fit);
    // The live end follows a new frame; any other position stays.
    this.player.setMax(fit === null ? 0 : fit.frames.length, true);
    this.binding.update();
    this.d.changed();
  }

  private islandHtml(t: Timelapse): string {
    return transportHtml(this.player, this.stamp(this.player.frame))
      + ticksHtml(dayTicks(t.frames[0].tSec, this.nowSec(), TIMELAPSE_WAR_TICKS, true))
      + `<div class="tlnote">${esc(T.war_note)}</div>`;
  }

  /** The world second of the live map: the season clock of the store, and
   *  never a second before the newest frame or the newest sample of the
   *  series, because the store's clock re-anchors on the scoreboard alone. */
  private nowSec(): number {
    const s = this.d.state;
    const t = this.tl;
    const last = t === null ? 0 : t.frames[t.frames.length - 1].tSec;
    const sov = s.war?.sov;
    const sample = sov && sov.length > 0 ? sov[sov.length - 1].tSec : 0;
    return Math.max(s.seasonElapsedSec(), last, sample);
  }

  /** The stamp of one position: the frame, or NOW at the live end. */
  private stamp(frame: number): string {
    const t = this.tl;
    if (t === null || frame >= t.frames.length) return T.stamp_now({ day: seasonDay(this.nowSec()) });
    return frameStamp(t.frames[frame].tSec);
  }

  /** Moves the map, the band and the tab to the player's position. */
  private follow(): void {
    const t = this.tl;
    const i = t !== null && this.player.frame < t.frames.length ? this.player.frame : -1;
    if (i === this.shown) return;
    const was = this.shown >= 0;
    this.shown = i;
    if (t === null || i < 0) {
      this.view_ = { frame: null };
      this.map?.show(null);
      this.band.hidden = true;
    } else {
      const f = t.frames[i];
      this.owners = ownersOf(f.cells, this.owners ?? undefined);
      const frame = { stamp: frameStamp(f.tSec), tSec: f.tSec, counts: frameCounts(f.cells) };
      this.view_ = { frame };
      this.map?.show(this.owners);
      const day = this.band.querySelector('.tlday');
      if (day) day.textContent = frame.stamp;
      this.band.hidden = false;
    }
    if (was !== i >= 0) this.d.focus?.(i >= 0);
    this.d.changed();
  }
}
